import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'tabler-icons-react';
import {
  ActionIcon,
  Button,
  Divider,
  NumberFormatter,
  Select,
  Skeleton,
  TextInput
} from '@mantine/core';
import PropTypes from 'prop-types';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppFlexbox from '../../common/AppFlexbox';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import AppCard from '../../common/AppCard';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import { useAdminBackPath } from '../../../helpers/adminHelper';
import { triggerNotification } from '../../../helpers/notification';
import { PAYMENT_TYPE_ENUM } from '../../../config/constants';
import StoreOverviewInfoCard from '../stores/StoreOverviewInfoCard';
import AdminStoreEscrowPaymentConfirmModal from './AdminStoreEscrowPaymentConfirmModal';

const STORE_ESCROW_PAYMENT_TYPE_OPTIONS = [
  {
    value: PAYMENT_TYPE_ENUM.CHEQUE,
    label: 'Cheque'
  },
  {
    value: PAYMENT_TYPE_ENUM.E_TRANSFER,
    label: 'E-Transfer'
  },
  {
    value: PAYMENT_TYPE_ENUM.CASH,
    label: 'Cash'
  }
];

const AdminStoreEscrowPayoutCreateView = ({ loading, ecomStoreDetails }) => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const { pkEcomStore } = useParams();
  const { isTabletOrSmaller } = useMediaQueryIndex();
  const { createOfflineEcomStoreEscrowPayment } = useContext(AdminContext);
  const { state, fetchStoreEscrowSummary } = useContext(StoreContext);
  const { backPath } = useAdminBackPath(`/admin/stores/${pkEcomStore}`);
  const [formState, setFormState] = useState({
    fkRegFormSubmissionPaymentType: null,
    transactionId: '',
    showConfirm: false,
    loading: false
  });

  const storeEscrowSummary =
    ecomStoreDetails?.ecomStore.pkEcomStore.toString() ===
    state.ecomStoreEscrowSummary.value?.ecomStore.pkEcomStore.toString()
      ? state.ecomStoreEscrowSummary.value
      : null;

  const escrowLoading =
    !hasFetched.current ||
    state.ecomStoreEscrowSummary.loading ||
    !storeEscrowSummary;

  const claimableBalance = storeEscrowSummary
    ? storeEscrowSummary.escrowEarned - storeEscrowSummary.escrowAmountClaimed
    : 0;

  useEffect(() => {
    if (ecomStoreDetails) {
      fetchStoreEscrowSummary(
        ecomStoreDetails.ecomStore.pkEcomStore,
        null,
        triggerNotification
      );
      setFormState({
        fkRegFormSubmissionPaymentType:
          STORE_ESCROW_PAYMENT_TYPE_OPTIONS[0].value,
        transactionId: '',
        showConfirm: false,
        loading: false
      });
      hasFetched.current = true;
    }
  }, [ecomStoreDetails]);

  const onSubmitEscrowPayment = () => {
    setFormState((c) => ({
      ...c,
      loading: true,
      showConfirm: false
    }));
    createOfflineEcomStoreEscrowPayment(
      storeEscrowSummary.ecomStore.pkEcomStore,
      {
        fkRegFormSubmissionPaymentType:
          formState.fkRegFormSubmissionPaymentType,
        transactionId: formState.transactionId
      },
      () => {
        triggerNotification('Escrow payment recorded.', 'success');
        navigate(backPath);
      },
      (error) => {
        setFormState((c) => ({
          ...c,
          loading: false
        }));
        triggerNotification(error);
      }
    );
  };

  return !loading && !escrowLoading && ecomStoreDetails ? (
    <AppStack
      p={{ base: 8, sm: '16px 16px 80px 16px' }}
      style={{
        flex: 1,
        margin: 'auto',
        width: '100%',
        maxWidth: 1050,
        paddingTop: 16,
        gap: 16
      }}
    >
      <AppFlexbox
        p={{ base: '0px 8px', sm: 0 }}
        style={{
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <AppFlexbox style={{ gap: 8 }}>
          <ActionIcon
            color="dark"
            component={Link}
            radius="md"
            style={{ marginTop: 5 }}
            to={backPath}
            variant="subtle"
          >
            <ArrowLeft />
          </ActionIcon>

          <AppStack style={{ gap: 0 }}>
            <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
              <AppText
                style={{
                  flex: 1,
                  fontSize: 24,
                  fontWeight: 700,
                  wordBreak: 'break-word'
                }}
              >
                Record escrow payment
              </AppText>
            </AppFlexbox>

            <AppText
              style={{
                fontSize: 12,
                lineHeight: '16px',
                color: '#666'
              }}
            >
              Record escrow payment for {ecomStoreDetails.ecomStore.name}
            </AppText>
          </AppStack>
        </AppFlexbox>
      </AppFlexbox>

      <AppFlexbox
        style={{ flexDirection: isTabletOrSmaller ? 'column-reverse' : 'row' }}
      >
        <AppStack style={{ flex: 1 }}>
          <AppStack style={{ gap: 16 }}>
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ flex: 1, padding: 0 }}
              withBorder
            >
              <AppStack style={{ gap: 8, padding: 16 }}>
                <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                  Balance
                </AppText>
              </AppStack>
              <Divider />
              <AppStack style={{ padding: 16, gap: 8 }}>
                <AppFlexbox
                  style={{
                    gap: 8,
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <AppText style={{ fontSize: 14 }}>Profit</AppText>
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14 }}
                    thousandSeparator
                    value={storeEscrowSummary.storeProfit / 100}
                  />
                </AppFlexbox>

                <AppFlexbox
                  style={{
                    gap: 8,
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <AppText style={{ fontSize: 14 }}>Tax</AppText>
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14 }}
                    thousandSeparator
                    value={storeEscrowSummary.storeTax / 100}
                  />
                </AppFlexbox>
                <Divider />

                <AppFlexbox
                  style={{
                    gap: 8,
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Total earned
                  </AppText>
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14, fontWeight: 500 }}
                    thousandSeparator
                    value={storeEscrowSummary.escrowEarned / 100}
                  />
                </AppFlexbox>

                <AppFlexbox
                  style={{
                    gap: 8,
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <AppText style={{ fontSize: 14 }}>Amount received</AppText>
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14 }}
                    thousandSeparator
                    value={storeEscrowSummary.escrowAmountClaimed / 100}
                  />
                </AppFlexbox>
                <Divider />

                <AppFlexbox
                  style={{
                    gap: 8,
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Balance to pay
                  </AppText>
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14, fontWeight: 500 }}
                    thousandSeparator
                    value={claimableBalance / 100}
                  />
                </AppFlexbox>
              </AppStack>
            </AppCard>
          </AppStack>
        </AppStack>

        <AppStack
          style={{ flex: 1, maxWidth: isTabletOrSmaller ? 'unset' : 350 }}
        >
          <StoreOverviewInfoCard
            ecomStore={ecomStoreDetails.ecomStore}
            showDefaultAddress
          />

          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 0 }}
            withBorder
          >
            <AppStack
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                setFormState({
                  ...formState,
                  showConfirm: true
                });
              }}
              style={{ gap: 0 }}
            >
              <AppFlexbox style={{ padding: 16 }}>
                <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                  Record payment
                </AppText>
              </AppFlexbox>

              <Divider />

              <AppStack
                style={{ gap: 16, padding: 16, backgroundColor: '#f1f1f1' }}
              >
                <AppFlexbox
                  style={{
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Total
                  </AppText>

                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 14, fontWeight: 500 }}
                    thousandSeparator
                    value={claimableBalance / 100}
                  />
                </AppFlexbox>
              </AppStack>

              <Divider />

              <AppStack style={{ gap: 16, padding: 16 }}>
                <Select
                  data={STORE_ESCROW_PAYMENT_TYPE_OPTIONS}
                  disabled={formState.loading}
                  label="Payment type"
                  onChange={(value) =>
                    setFormState((prevState) => ({
                      ...prevState,
                      fkRegFormSubmissionPaymentType: value
                    }))
                  }
                  placeholder="Select payment type"
                  required
                  value={formState.fkRegFormSubmissionPaymentType || null}
                />

                <TextInput
                  description="Enter a transaction ID to easily track the payment. If left empty, one will be generated automatically."
                  disabled={formState.loading}
                  label="Transaction ID"
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      transactionId: e.currentTarget.value
                    })
                  }
                  placeholder="Enter transaction ID"
                  required={false}
                  value={formState.transactionId || ''}
                />
              </AppStack>

              <Divider />

              <AppStack style={{ gap: 16, padding: 16 }}>
                <Button
                  color="dark"
                  disabled={claimableBalance <= 0}
                  loading={formState.loading}
                  type="submit"
                  variant="filled"
                >
                  Continue to preview
                </Button>
              </AppStack>
            </AppStack>
          </AppCard>
        </AppStack>
      </AppFlexbox>

      <AdminStoreEscrowPaymentConfirmModal
        fkRegFormSubmissionPaymentType={
          formState.fkRegFormSubmissionPaymentType
        }
        isOpen={formState.showConfirm}
        loading={formState.loading}
        onClose={() =>
          setFormState((prevState) => ({
            ...prevState,
            showConfirm: false
          }))
        }
        onSubmit={onSubmitEscrowPayment}
        storeEscrowSummary={storeEscrowSummary}
        transactionId={formState.transactionId}
      />
    </AppStack>
  ) : (
    <AppStack
      p={{ base: 8, sm: '16px 16px 80px 16px' }}
      style={{
        flex: 1,
        margin: 'auto',
        width: '100%',
        maxWidth: 1050,
        paddingTop: 16,
        gap: 16
      }}
    >
      <AppFlexbox
        p={{ base: '0px 8px', sm: 0 }}
        style={{
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <AppFlexbox style={{ gap: 8 }}>
          <ActionIcon
            color="dark"
            component={Link}
            radius="md"
            style={{ marginTop: 5 }}
            to={backPath}
            variant="subtle"
          >
            <ArrowLeft />
          </ActionIcon>

          <AppStack style={{ gap: 0 }}>
            <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
              <AppText
                style={{
                  flex: 1,
                  fontSize: 24,
                  fontWeight: 700,
                  wordBreak: 'break-word'
                }}
              >
                Record escrow payment
              </AppText>
            </AppFlexbox>

            <Skeleton height={12} width={200} />
          </AppStack>
        </AppFlexbox>
      </AppFlexbox>

      <AppFlexbox
        style={{ flexDirection: isTabletOrSmaller ? 'column-reverse' : 'row' }}
      >
        <AppStack style={{ flex: 1 }}>
          <AppStack style={{ gap: 8 }}>
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ flex: 1, padding: 0 }}
              withBorder
            >
              <AppStack style={{ gap: 8, padding: 16 }}>
                <Skeleton height={18} width={80} />
              </AppStack>
              <Divider />
              <AppStack style={{ padding: 16, gap: 8 }}>
                {Array.from(Array(2)).map((_, index) => (
                  <AppFlexbox
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    style={{
                      gap: 8,
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Skeleton height={16} width={90} />
                    <Skeleton height={16} width={35} />
                  </AppFlexbox>
                ))}

                <Divider />

                {Array.from(Array(2)).map((_, index) => (
                  <AppFlexbox
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    style={{
                      gap: 8,
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Skeleton height={16} width={90} />
                    <Skeleton height={16} width={35} />
                  </AppFlexbox>
                ))}

                <Divider />

                <AppFlexbox
                  style={{
                    gap: 8,
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Skeleton height={16} width={110} />
                  <Skeleton height={16} width={35} />
                </AppFlexbox>
              </AppStack>
            </AppCard>
          </AppStack>
        </AppStack>

        <AppStack
          style={{ flex: 1, maxWidth: isTabletOrSmaller ? 'unset' : 350 }}
        >
          <StoreOverviewInfoCard loading showDefaultAddress />

          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 0 }}
            withBorder
          >
            <AppStack
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
              }}
              style={{ gap: 0 }}
            >
              <AppFlexbox style={{ padding: 16 }}>
                <Skeleton height={18} width={120} />
              </AppFlexbox>

              <Divider />

              <AppStack
                style={{ gap: 16, padding: 16, backgroundColor: '#f1f1f1' }}
              >
                <AppFlexbox
                  style={{
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Skeleton height={16} width={40} />

                  <Skeleton height={16} width={50} />
                </AppFlexbox>
              </AppStack>

              <Divider />

              <AppStack style={{ gap: 16, padding: 16 }}>
                <AppStack style={{ gap: 3 }}>
                  <Skeleton height={12} width="80" />
                  <Skeleton height={44} width="100%" />
                </AppStack>

                <AppStack style={{ gap: 5 }}>
                  <Skeleton height={12} width="80" />
                  <Skeleton height={8} width="100%" />
                  <Skeleton height={8} width="80%" />
                  <Skeleton height={44} width="100%" />
                </AppStack>
              </AppStack>

              <Divider />

              <AppStack style={{ gap: 16, padding: 16 }}>
                <Skeleton height={32} width="100%" />
              </AppStack>
            </AppStack>
          </AppCard>
        </AppStack>
      </AppFlexbox>
    </AppStack>
  );
};

AdminStoreEscrowPayoutCreateView.propTypes = {
  loading: PropTypes.bool.isRequired,
  ecomStoreDetails: PropTypes.object
};

export default AdminStoreEscrowPayoutCreateView;
